import React from 'react';
import {
  VictoryChart,
  VictoryBar,
  VictoryLine,
  VictoryAxis,
  VictoryTheme,
  VictoryLegend,
  VictoryScatter,
  VictoryLabel,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import { formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 07: Solvencia
 *
 * Combined bar and line chart:
 * - Bars: Total Activos (in millions USD)
 * - Line: Ratio de Capital Global (percentage on secondary axis)
 *
 * Matches PDF report format from Atuntaqui_mar2025
 */

export interface Grafico07V2Data {
  period: string;              // YYYY-MM format
  activo: number;              // Total assets in USD
  ratio_capital_global: number; // Capital adequacy ratio (e.g., 0.1531 = 15.31%)
}

export interface Grafico07V2Props {
  data: Grafico07V2Data[];
  title?: string;
}

export const Grafico07V2: React.FC<Grafico07V2Props> = ({
  data,
  title = 'Gráfico 07: Solvencia'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Convert to millions for bars
  const toMillions = (value: number): number => value / 1_000_000;

  // Bar data (Activos in millions) - no labels for clean display
  const barData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: toMillions(d.activo),
  }));

  // Line data (Ratio de Capital Global as percentage) - NO labels to avoid duplicates
  const lineData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.ratio_capital_global * 100,
  }));

  // Scatter data has labels for display
  const scatterData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.ratio_capital_global * 100,
    label: `${(d.ratio_capital_global * 100).toFixed(1)}%`
  }));

  // Calculate domains
  const maxActivo = Math.max(...barData.map(d => d.y));
  const maxRatio = Math.max(...lineData.map(d => d.y));

  // Left axis domain (Activos in millions)
  const leftAxisMax = Math.ceil((maxActivo * 1.1) / 200) * 200; // Round to nearest 200

  // Right axis domain (Ratio: fixed at 20% max as per PDF format)
  const rightAxisMax = 20; // Fixed 0-20% scale

  // Scale factor to map ratio values to activo axis
  const scaleFactor = leftAxisMax / rightAxisMax;

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper chart-dual-axis">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 30, y: 10 }}
          padding={{ top: 50, bottom: 60, left: 55, right: 55 }}
          height={300}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) => datum.label}
              labelComponent={<VictoryTooltip style={{ fontSize: 9 }} />}
            />
          }
        >
          {/* Legend */}
          <VictoryLegend
            x={80}
            y={5}
            orientation="horizontal"
            gutter={30}
            style={{ labels: { fontSize: 9 } }}
            data={[
              { name: 'Activo', symbol: { fill: COLORS.secondary } },
              { name: 'Ratio de Capital Global', symbol: { fill: COLORS.primary, type: 'minus' } },
            ]}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              }
            }}
          />

          {/* Y-Axis Left (Millones USD) */}
          <VictoryAxis
            dependentAxis
            domain={[0, leftAxisMax]}
            tickFormat={(t: number) => `${t.toFixed(0)}`}
            label="Millones"
            style={{
              axis: { stroke: '#666' },
              axisLabel: { fontSize: 9, padding: 40 },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Y-Axis Right (Percentage) - shows ratio values */}
          <VictoryAxis
            dependentAxis
            orientation="right"
            domain={[0, leftAxisMax]}
            tickValues={[0, leftAxisMax * 0.25, leftAxisMax * 0.5, leftAxisMax * 0.75, leftAxisMax]}
            tickFormat={(t: number) => `${(t / scaleFactor).toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 }
            }}
          />

          {/* Activo Bars - teal */}
          <VictoryBar
            data={barData}
            barWidth={12}
            style={{
              data: { fill: COLORS.secondary }
            }}
          />

          {/* Ratio Capital Global Line - scaled to activo axis - dark gray */}
          <VictoryLine
            data={lineData.map(d => ({
              x: d.x,
              y: d.y * scaleFactor
            }))}
            style={{
              data: {
                stroke: COLORS.primary,
                strokeWidth: 1.2
              }
            }}
          />

          {/* Ratio Points with small value labels - dark gray */}
          <VictoryScatter
            data={scatterData.map(d => ({
              ...d,
              y: d.y * scaleFactor
            }))}
            size={3}
            style={{ data: { fill: COLORS.primary } }}
            labelComponent={<VictoryLabel dy={-8} style={{ fontSize: 8, fill: COLORS.primary }} />}
          />
        </VictoryChart>
      </div>

      <p className="chart-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Grafico07V2;
